import { z } from "zod";
import { pctStringSchema } from "./clinical.ts";

/**
 * Pharmacy inventory: the drug stock a pharmacy tenant manages. One row per
 * stocked product with on-hand quantity, reorder level, sell price and an
 * optional expiry. Low-stock / expiring status is computed (pure) from these.
 */

export const pharmacyFormSchema = z.enum([
	"tablet",
	"capsule",
	"syrup",
	"injection",
	"cream",
	"drops",
	"inhaler",
	"suppository",
	"other",
]);
export type PharmacyForm = z.infer<typeof pharmacyFormSchema>;

export const pharmacyItemApiSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	name: z.string(),
	form: pharmacyFormSchema,
	/** Pack/unit label, e.g. "box of 20", "100 ml". */
	unit: z.string().nullable(),
	barcode: z.string().nullable(),
	sell_price: z.number().nullable(),
	cost_price: z.number().nullable(),
	quantity_on_hand: z.number().int(),
	reorder_level: z.number().int(),
	expiry_date: z
		.string()
		.regex(/^\d{4}-\d{2}-\d{2}$/)
		.nullable(),
	notes: z.string().nullable(),
	created_at: z.string().datetime(),
	updated_at: z.string().datetime(),
});
export type PharmacyItemApi = z.infer<typeof pharmacyItemApiSchema>;

export const createPharmacyItemInputSchema = z.object({
	name: z.string().min(1).max(200),
	form: pharmacyFormSchema.default("tablet"),
	unit: z.string().max(60).optional(),
	barcode: z.string().max(60).optional(),
	sell_price: z.coerce.number().nonnegative().max(99_999_999).optional(),
	cost_price: z.coerce.number().nonnegative().max(99_999_999).optional(),
	quantity_on_hand: z.coerce.number().int().min(0).max(10_000_000).default(0),
	reorder_level: z.coerce.number().int().min(0).max(1_000_000).default(0),
	expiry_date: z
		.string()
		.regex(/^\d{4}-\d{2}-\d{2}$/)
		.optional(),
	notes: z.string().max(500).optional(),
});
export type CreatePharmacyItemInput = z.infer<
	typeof createPharmacyItemInputSchema
>;

export const updatePharmacyItemInputSchema = z.object({
	name: z.string().min(1).max(200).optional(),
	form: pharmacyFormSchema.optional(),
	unit: z.string().max(60).nullable().optional(),
	barcode: z.string().max(60).nullable().optional(),
	sell_price: z.coerce
		.number()
		.nonnegative()
		.max(99_999_999)
		.nullable()
		.optional(),
	cost_price: z.coerce
		.number()
		.nonnegative()
		.max(99_999_999)
		.nullable()
		.optional(),
	reorder_level: z.coerce.number().int().min(0).max(1_000_000).optional(),
	expiry_date: z
		.string()
		.regex(/^\d{4}-\d{2}-\d{2}$/)
		.nullable()
		.optional(),
	notes: z.string().max(500).nullable().optional(),
});
export type UpdatePharmacyItemInput = z.infer<
	typeof updatePharmacyItemInputSchema
>;

/**
 * Adjust on-hand quantity by a signed delta (receive stock = +, dispense /
 * wastage = -). Reason is recorded for the stock-movement audit.
 */
export const adjustPharmacyStockInputSchema = z.object({
	item_id: z.string().uuid(),
	delta: z.coerce
		.number()
		.int()
		.refine((n) => n !== 0, "delta must be non-zero"),
	reason: z
		.enum(["received", "dispensed", "wastage", "correction", "expired"])
		.default("correction"),
	notes: z.string().max(300).optional(),
});
export type AdjustPharmacyStockInput = z.infer<
	typeof adjustPharmacyStockInputSchema
>;

export type StockStatus = "out" | "low" | "ok";

/** Stock level vs reorder threshold. Pure. */
export function stockStatus(
	quantityOnHand: number,
	reorderLevel: number,
): StockStatus {
	if (quantityOnHand <= 0) return "out";
	if (quantityOnHand <= reorderLevel) return "low";
	return "ok";
}

/** True when an expiry date is within `days` (default 30) of `asOf`. Pure. */
export function isExpiringSoon(
	expiryDate: string | null | undefined,
	asOfIso: string,
	days = 30,
): boolean {
	if (!expiryDate) return false;
	const exp = Date.parse(`${expiryDate}T00:00:00Z`);
	const asOf = Date.parse(`${asOfIso.slice(0, 10)}T00:00:00Z`);
	if (!Number.isFinite(exp) || !Number.isFinite(asOf)) return false;
	const diffDays = (exp - asOf) / 86_400_000;
	return diffDays <= days; // includes already-expired (negative)
}

// ===== Point of Sale (POS) =====

export const pharmacyPaymentMethodSchema = z.enum([
	"cash",
	"card",
	"transfer",
	"other",
]);
export type PharmacyPaymentMethod = z.infer<typeof pharmacyPaymentMethodSchema>;

/** One cart line. unit_price is optional — defaults to the item's sell_price. */
export const createSaleItemInputSchema = z.object({
	item_id: z.string().uuid(),
	quantity: z.coerce.number().int().min(1).max(1_000_000),
	unit_price: z.coerce.number().nonnegative().max(99_999_999).optional(),
});
export type CreateSaleItemInput = z.infer<typeof createSaleItemInputSchema>;

export const createSaleInputSchema = z.object({
	patient_id: z.string().uuid().nullable().optional(),
	prescription_id: z.string().uuid().nullable().optional(),
	/**
	 * Set when the sale is rung up from an ACCEPTED referred prescription (the
	 * rx lives in the referring clinic's tenant). The server derives the
	 * referring clinic from it and snapshots the commission % onto the sale.
	 */
	source_prescription_id: z.string().uuid().nullable().optional(),
	/** Per-sale commission override. Omitted = the link's default; null = none. */
	referral_commission_pct: pctStringSchema.nullable().optional(),
	payment_method: pharmacyPaymentMethodSchema.default("cash"),
	discount_amount: z.coerce.number().nonnegative().max(99_999_999).default(0),
	paid_amount: z.coerce.number().nonnegative().max(99_999_999).optional(),
	notes: z.string().max(500).optional(),
	items: z.array(createSaleItemInputSchema).min(1).max(100),
});
export type CreateSaleInput = z.infer<typeof createSaleInputSchema>;

export const voidSaleInputSchema = z.object({
	reason: z.string().max(300).optional(),
});
export type VoidSaleInput = z.infer<typeof voidSaleInputSchema>;

export const pharmacySaleItemApiSchema = z.object({
	id: z.string().uuid(),
	item_id: z.string().uuid().nullable(),
	name: z.string(),
	quantity: z.number().int(),
	unit_price: z.number(),
	line_total: z.number(),
});
export type PharmacySaleItemApi = z.infer<typeof pharmacySaleItemApiSchema>;

export const pharmacySaleApiSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	sale_number: z.string(),
	patient_id: z.string().uuid().nullable(),
	patient_name: z.string().nullable().optional(),
	prescription_id: z.string().uuid().nullable(),
	subtotal: z.number(),
	discount_amount: z.number(),
	total_amount: z.number(),
	currency: z.string(),
	payment_method: pharmacyPaymentMethodSchema,
	paid_amount: z.number(),
	/** paid_amount − total_amount, never negative (change due to the customer). */
	change_amount: z.number(),
	status: z.enum(["completed", "voided"]),
	notes: z.string().nullable(),
	share_token: z.string(),
	sold_by_member_id: z.string().uuid().nullable(),
	voided_at: z.string().datetime().nullable(),
	created_at: z.string().datetime(),
	/** Referral commission linkage (sale from an accepted referred rx). */
	referring_clinic_id: z.string().uuid().nullable().optional(),
	source_prescription_id: z.string().uuid().nullable().optional(),
	referral_commission_pct: z.string().nullable().optional(),
	items: z.array(pharmacySaleItemApiSchema),
});
export type PharmacySaleApi = z.infer<typeof pharmacySaleApiSchema>;

export const pharmacySalesQuerySchema = z.object({
	from: z.string().datetime().optional(),
	to: z.string().datetime().optional(),
	limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type PharmacySalesQuery = z.infer<typeof pharmacySalesQuerySchema>;

/** Daily Z-report — totals for completed sales in a day. */
export const pharmacySalesReportSchema = z.object({
	date: z.string(),
	sales_count: z.number().int(),
	gross: z.number(), // sum of subtotals
	discount: z.number(), // sum of discounts
	net: z.number(), // sum of totals (what was actually charged)
	cogs: z.number(), // cost of goods sold (sum of line unit_cost × qty)
	profit: z.number(), // net − cogs
	margin_pct: z.number(), // profit / net × 100 (0 when net is 0)
	by_method: z.record(pharmacyPaymentMethodSchema, z.number()),
});
export type PharmacySalesReportApi = z.infer<typeof pharmacySalesReportSchema>;

// ===== Procurement (suppliers + purchase orders) =====

export const pharmacySupplierApiSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	name: z.string(),
	phone: z.string().nullable(),
	notes: z.string().nullable(),
	is_active: z.boolean(),
	created_at: z.string().datetime(),
});
export type PharmacySupplierApi = z.infer<typeof pharmacySupplierApiSchema>;

export const createSupplierInputSchema = z.object({
	name: z.string().min(1).max(200),
	phone: z.string().max(40).optional(),
	notes: z.string().max(500).optional(),
});
export type CreateSupplierInput = z.infer<typeof createSupplierInputSchema>;

export const updateSupplierInputSchema = z.object({
	name: z.string().min(1).max(200).optional(),
	phone: z.string().max(40).nullable().optional(),
	notes: z.string().max(500).nullable().optional(),
	is_active: z.boolean().optional(),
});
export type UpdateSupplierInput = z.infer<typeof updateSupplierInputSchema>;

export const purchaseOrderStatusSchema = z.enum([
	"draft",
	"ordered",
	"received",
	"cancelled",
]);
export type PurchaseOrderStatus = z.infer<typeof purchaseOrderStatusSchema>;

export const createPoItemInputSchema = z.object({
	item_id: z.string().uuid(),
	quantity: z.coerce.number().int().min(1).max(10_000_000),
	unit_cost: z.coerce.number().nonnegative().max(99_999_999).default(0),
});
export type CreatePoItemInput = z.infer<typeof createPoItemInputSchema>;

export const createPurchaseOrderInputSchema = z.object({
	supplier_id: z.string().uuid().nullable().optional(),
	notes: z.string().max(500).optional(),
	items: z.array(createPoItemInputSchema).min(1).max(200),
});
export type CreatePurchaseOrderInput = z.infer<
	typeof createPurchaseOrderInputSchema
>;

export const pharmacyPoItemApiSchema = z.object({
	id: z.string().uuid(),
	item_id: z.string().uuid().nullable(),
	name: z.string(),
	quantity: z.number().int(),
	unit_cost: z.number(),
	line_total: z.number(),
});
export type PharmacyPoItemApi = z.infer<typeof pharmacyPoItemApiSchema>;

export const pharmacyPurchaseOrderApiSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	po_number: z.string(),
	supplier_id: z.string().uuid().nullable(),
	supplier_name: z.string().nullable().optional(),
	status: purchaseOrderStatusSchema,
	total_cost: z.number(),
	currency: z.string(),
	notes: z.string().nullable(),
	ordered_at: z.string().datetime().nullable(),
	received_at: z.string().datetime().nullable(),
	cancelled_at: z.string().datetime().nullable(),
	created_at: z.string().datetime(),
	items: z.array(pharmacyPoItemApiSchema),
});
export type PharmacyPurchaseOrderApi = z.infer<
	typeof pharmacyPurchaseOrderApiSchema
>;
